import React, { Component } from "react";
import ReactMapGL, { NavigationControl, Marker, Popup } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import ResizeObserver from "resize-observer-polyfill";
import { MdLocationOn } from "react-icons/md";
import PropTypes from "prop-types";
import LocationInfo from "./location-info";
import ContentError from "./content-error";

const API_KEY = process.env.REACT_APP_MAPBOX_API_KEY;

class Map extends Component {
  constructor(props) {
    super(props);
    this.mapRef = React.createRef();
  }

  static propTypes = {
    locations: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        latitude: PropTypes.number.isRequired,
        longitude: PropTypes.number.isRequired
      })
    ).isRequired,
    selectedLocation: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      latitude: PropTypes.number.isRequired,
      longitude: PropTypes.number.isRequired
    }),
    onLocationSelected: PropTypes.func.isRequired,
    onMapLoaded: PropTypes.func.isRequired
  };

  state = {
    viewport: {
      latitude: 52.492,
      longitude: 13.344,
      zoom: 11.5,
      bearing: 0,
      pitch: 0,
      width: 600,
      height: 600
    },
    hasError: false,
    isMapLoaded: false,
    errorMesage: null // Used for the case of incorrect Mapbox API token
  };

  componentDidMount() {
    // A workaround for an issue that ReactMapGL gets only
    // fixed width and height (no CSS-style 100%)
    new ResizeObserver(this.resize).observe(document.getElementById("map"));

    // Workaround to fix the issue that container div generated by
    // ReactMapGL comp is set to "1"
    document.getElementById("map").firstElementChild.tabIndex = -1;

    // This is a workaround to specially handle the case of incorrect
    // Mapbox API token. And that's because neither onLoad nor onError
    // events are fired by ReactMapGL in that case
    this.mapRef.current.getMap().on("error", this.handleMapboxError);

    // If ReactMapGL didn't fire the onLoad event or onError within
    // 15 seconds, consider this as a timeout, and treat as error
    setTimeout(() => {
      if (!this.state.isMapLoaded) {
        this.setState({
          isMapLoaded: true,
          hasError: true
        });
        this.props.onMapLoaded();
      }
    }, 15000);
  }

  // A workaround for an issue with the ReactMapGL getting only
  // fixed width and height (no CSS-style 100%)
  resize = () => {
    const map = document.getElementById("map");

    this.setState({
      viewport: {
        ...this.state.viewport,
        width: map.offsetWidth - 1,
        height: map.offsetHeight
      }
    });
  };

  // This is a workaround to specially handle the case of incorrect
  // Mapbox API token. And that's because neither onLoad nor onError
  // events are fired by ReactMapGL in that case
  handleMapboxError = e => {
    if (e.error.status === 401 && e.error.message.startsWith("Unauthorized:")) {
      this.mapRef.current.getMap().off("error", this.handleMapboxError);
      this.handleOnError(e.error.message);
    }
  };

  handleOnError = (message = null) => {
    this.setState({
      hasError: true,
      isMapLoaded: true,
      errorMesage: message
    });
    this.props.onMapLoaded();
  };

  handleMapLoaded = () => {
    this.setState({
      isMapLoaded: true
    });
    this.props.onMapLoaded();
  };

  render() {
    return (
      <div
        id="map"
        role="application"
        aria-label="Map with locations"
        style={{ height: "100%", width: "100%" }}
      >
        {this.state.hasError && (
          <ContentError errorMesage={this.state.errorMesage} />
        )}

        {!this.state.hasError && (
          <ReactMapGL
            ref={this.mapRef}
            mapboxApiAccessToken={API_KEY}
            mapStyle="mapbox://styles/mapbox/streets-v10?optimize=true"
            {...this.state.viewport}
            onViewportChange={viewport => this.setState({ viewport })}
            onLoad={this.handleMapLoaded}
            onError={this.handleOnError}
            touchRotate={true}
            getCursor={({ isDragging }) => (isDragging ? "move" : "default")}
          >
            {this.props.locations.map(location => (
              <Marker
                key={location.id}
                longitude={location.longitude}
                latitude={location.latitude}
              >
                <span>
                  <MdLocationOn
                    className={
                      this.props.selectedLocation &&
                      this.props.selectedLocation.id === location.id
                        ? "map-marker map-marker--selected"
                        : "map-marker"
                    }
                    onClick={() => this.props.onLocationSelected(location)}
                  />
                </span>
              </Marker>
            ))}

            {this.props.selectedLocation && (
              <Popup
                tipSize={10}
                anchor="top"
                longitude={this.props.selectedLocation.longitude}
                latitude={this.props.selectedLocation.latitude}
                onClose={() => this.props.onLocationSelected(null)}
                closeOnClick={false}
              >
                <LocationInfo location={this.props.selectedLocation} />
              </Popup>
            )}

            <div className="nav map-nav-control">
              <NavigationControl
                onViewportChange={viewport => this.setState({ viewport })}
              />
            </div>
          </ReactMapGL>
        )}
      </div>
    );
  }
}

export default Map;
